import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Navigate } from '@ngxs/router-plugin';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { MessageService } from 'primeng/api';

import { AppPagesEnum } from '@customer-portal/shared/constants';
import {
  CalendarViewType,
  ToastSeverityIcons,
} from '@customer-portal/shared/models';
import { FilterValue } from '@customer-portal/shared/models/grid';

import {
  ClearFinancialStatusNavigation,
  ClearNavigationFilters,
  OverviewFinancialNavigateToListView,
  OverviewUpcomingSetSelectedDate,
  ResetOverviewSharedState,
  SetFinancialStatusChartNavigationPayload,
  SetNavigationFilters,
} from './actions';

export interface OverviewSharedStateModel {
  auditCalendarSelectedDate: Date | undefined;
  auditCalendarViewType: CalendarViewType | string;
  financialStatusSelectedState: FilterValue[];
  chartNavigationPayload: FilterValue[];
  navigationFilters?: FilterValue[];
}

const defaultState: OverviewSharedStateModel = {
  auditCalendarSelectedDate: undefined,
  auditCalendarViewType: '',
  financialStatusSelectedState: [
    {
      label: '',
      value: [],
    },
  ],
  chartNavigationPayload: [],
};

@State<OverviewSharedStateModel>({
  name: 'overviewShared',
  defaults: defaultState,
})
@Injectable()
export class OverviewSharedState {
  private readonly ts = inject(TranslocoService);
  private readonly messageService = inject(MessageService);

  @Selector([OverviewSharedState])
  static getSelectedFinancialStatus(
    state: OverviewSharedStateModel,
  ): FilterValue[] {
    return state.financialStatusSelectedState;
  }

  @Action(OverviewUpcomingSetSelectedDate)
  setSelectedDate(
    ctx: StateContext<OverviewSharedStateModel>,
    action: OverviewUpcomingSetSelectedDate,
  ) {
    ctx.patchState({
      auditCalendarSelectedDate: action.date,
      auditCalendarViewType: action.calendarViewType,
    });

    if (action.date) {
      ctx.dispatch(new Navigate([AppPagesEnum.Schedule]));
    }
  }

  @Action(OverviewFinancialNavigateToListView)
  navigateFromChartToListView(
    ctx: StateContext<OverviewSharedStateModel>,
    action: OverviewFinancialNavigateToListView,
  ) {
    try {
      ctx.patchState({
        financialStatusSelectedState: action.tooltipFilters,
      });
      ctx.dispatch(new Navigate([AppPagesEnum.Financials]));
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: this.ts.translate('toast.somethingWentWrong'),
        icon: ToastSeverityIcons.ExclamationCircle,
      });
    }
  }

  @Action(SetFinancialStatusChartNavigationPayload)
  setFinancialStatusChartNavigationPayload(
    ctx: StateContext<OverviewSharedStateModel>,
    { chartNavigationPayload }: SetFinancialStatusChartNavigationPayload,
  ) {
    ctx.patchState({
      chartNavigationPayload,
    });
  }

  @Action(ResetOverviewSharedState)
  resetOverviewSharedState(ctx: StateContext<OverviewSharedStateModel>) {
    ctx.setState(defaultState);
  }

  @Action(SetNavigationFilters)
  setNavigationFilters(
    ctx: StateContext<OverviewSharedStateModel>,
    action: SetNavigationFilters,
  ) {
    ctx.patchState({ navigationFilters: action.filters });
  }

  @Action(ClearNavigationFilters)
  clearNavigationFilters(ctx: StateContext<OverviewSharedStateModel>) {
    ctx.patchState({ navigationFilters: [] });
  }

  @Action(ClearFinancialStatusNavigation)
  clearFinancialStatusNavigation(ctx: StateContext<OverviewSharedStateModel>) {
    ctx.patchState({
      financialStatusSelectedState: defaultState.financialStatusSelectedState,
    });
  }
}
